// NEW: src/components/confetti-burst.js
//
// Pure-CSS confetti burst. Spawns 36 tiny rectangles that fall with rotation
// and horizontal drift, then unmount themselves after 2.5s. No libraries,
// no canvas — just absolutely-positioned spans with CSS animations.
//
// Self-contained lifecycle: the parent drops <ConfettiBurst /> into the tree,
// we time out, set `visible=false`, React removes the DOM. Re-mounting the
// component (e.g. user navigates back + forward) re-triggers a fresh burst.

import { useEffect, useMemo, useState } from "react";

const COLORS = ["#E8A817", "#1A7A52", "#FFFFFF", "#FFD6A0"];
const PIECES = 36;
const DURATION_MS = 2500;

function rand(min, max) { return Math.random() * (max - min) + min; }

function makePieces() {
  const out = [];
  for (let i = 0; i < PIECES; i++) {
    const color = COLORS[i % COLORS.length];
    const leftPct = rand(0, 100);
    const delayMs = rand(0, 400);          // stagger so they don't all hit the floor together
    const fallMs = rand(1800, 2500);       // slightly varied fall times
    const driftVW = rand(-20, 20);         // horizontal drift during fall (vw units)
    const rotStart = rand(0, 360);
    const rotEnd = rotStart + rand(360, 900);
    const w = rand(6, 10);
    const h = rand(10, 18);
    out.push({
      i, color, leftPct, delayMs, fallMs,
      driftVW, rotStart, rotEnd, w, h,
    });
  }
  return out;
}

export function ConfettiBurst({ duration = DURATION_MS }) {
  const pieces = useMemo(makePieces, []);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setVisible(false), duration);
    return () => clearTimeout(t);
  }, [duration]);

  if (!visible) return null;

  return (
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        top: 0, left: 0, right: 0, bottom: 0,
        pointerEvents: "none",
        overflow: "hidden",
        zIndex: 2,
      }}
    >
      {pieces.map(p => (
        <span
          key={p.i}
          style={{
            position: "absolute",
            top: -20,
            left: `${p.leftPct}%`,
            width: p.w,
            height: p.h,
            background: p.color,
            borderRadius: 2,
            opacity: 0.95,
            // CSS custom properties drive the keyframe below
            "--drift": `${p.driftVW}vw`,
            "--rot-start": `${p.rotStart}deg`,
            "--rot-end": `${p.rotEnd}deg`,
            animation: `samajh-confetti-fall ${p.fallMs}ms cubic-bezier(0.2, 0.6, 0.3, 1) ${p.delayMs}ms forwards`,
            willChange: "transform, opacity",
          }}
        />
      ))}
      <style>{`
        @keyframes samajh-confetti-fall {
          0% {
            transform: translate3d(0, 0, 0) rotate(var(--rot-start));
            opacity: 0;
          }
          8% { opacity: 1; }
          80% { opacity: 1; }
          100% {
            transform: translate3d(var(--drift), 110vh, 0) rotate(var(--rot-end));
            opacity: 0;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          /* Respect user preference — no animation, no confetti */
          [aria-hidden="true"] span { display: none; }
        }
      `}</style>
    </div>
  );
}

export default ConfettiBurst;
